import { Component, EventEmitter, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { VideoPlayerComponent } from './video-player/video-player.component';
import { VideoService, Video as ApiVideo } from './video.service';
import { ApiService } from '../services/api.service';
import { SearchService } from '../services/search.service';
import { Subject, combineLatest, of } from 'rxjs';
import { debounceTime, filter, switchMap, takeUntil, map, startWith } from 'rxjs/operators';
import { PaginationComponent } from '../shared/pagination/pagination.component';
import { DurationPipe } from '../pipe/duration.pipe';

interface Video {
  _id: string;
  title: string;
  description: string;
  url: string;
  thumbnail: string;
  module: string;
  date: string;
  views: number;
  duration: number;
  supportingFiles: string[];
  projectId: string;
  likesCount?: number;
  favoritesCount?: number;
  likedByCurrentUser?: boolean;
  favoritedByCurrentUser?: boolean;
  comments?: any[];
}

@Component({
  selector: 'app-video',
  imports: [CommonModule, VideoPlayerComponent, PaginationComponent, DurationPipe],
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
  standalone: true,
})
export class VideoComponent {
  currentModule = '';
  videos: Video[] = [];
  selectedVideo: Video | null = null;
  currentProjectId = '';
  currentSortOrder: 'asc' | 'desc' = 'desc';
  currentPage = 1;
  limit = 8;
  totalCount = 0;
  Math = Math;

  private videoService = inject(VideoService);
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private searchService = inject(SearchService);
  private destroy$ = new Subject<void>();

  @Output() playerStateChange = new EventEmitter<boolean>();

  ngOnInit() {
    // Get current module from parent route
    this.route.parent?.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.currentModule = params.get('module') || '';
    });

    // Watch route changes for selected video
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const videoId = params.get('id');
      if (videoId) {
        this.loadVideoById(videoId);
      } else if (this.currentModule) {
        this.loadProjectIdAndVideos(this.currentModule);
      }
    });
  }

  // ----------------- Project & Video Fetch -----------------
  private loadProjectIdAndVideos(moduleSlug: string) {
    this.videos = [];
    this.selectedVideo = null;

    this.api.get<any[]>('/projects').pipe(takeUntil(this.destroy$)).subscribe({
      next: projects => {
        const matched = projects.find(p => this.slugify(p.name) === moduleSlug);
        if (matched) {
          this.currentProjectId = matched._id;
          this.setupVideoStream();
        } else {
          console.warn('No project found for slug:', moduleSlug);
        }
      },
      error: err => console.error('Failed to fetch projects', err)
    });
  }

  // ----------------- Unified Reactive Stream -----------------
  private setupVideoStream() {
    // Combine search, module filter, and sort
    combineLatest([
      this.searchService.search$.pipe(debounceTime(500), startWith('')),
      this.searchService.moduleFilter$.pipe(startWith('all')),
      this.searchService.sortOrder$.pipe(startWith(this.currentSortOrder))
    ])
      .pipe(
        takeUntil(this.destroy$),
        switchMap(([searchTerm, moduleId, sortOrder]) => {
          if (!this.currentProjectId) return of({ videos: [], totalCount: 0 });
          this.currentSortOrder = sortOrder as 'asc' | 'desc';
          const search = searchTerm?.trim() || undefined;
          const moduleFilter = moduleId && moduleId !== 'all' ? moduleId : undefined;
          const offset = (this.currentPage - 1) * this.limit;
          return this.videoService.getVideosByProject(
            this.currentProjectId,
            search,
            moduleFilter,
            this.currentSortOrder,
            this.limit,
            offset
          );
        }),
        map((response) => ({
          videos: this.mapVideos(response.videos),
          totalCount: response.totalCount,
        }))
      )
      .subscribe({
        next: res => {
          this.videos = res.videos;
          this.totalCount = res.totalCount;
        },
        error: err => console.error('Failed to load videos', err)
      });
  }

  // ----------------- Helpers -----------------
  private mapVideos(videos: ApiVideo[]): Video[] {
    return videos.map(v => ({
      _id: v._id,
      title: v.title,
      description: v.description,
      url: v.url,
      thumbnail: v.thumbnailUrl,
      module: v.project?.name || '',
      date: new Date(v.createdAt).toLocaleDateString(),
      views: v.views ?? 0,
      duration: v.duration ?? 0,
      supportingFiles: v.supportingFiles,
      projectId: v.project?._id || ''
    }));
  }

  private slugify(name: string): string {
    return name.toLowerCase().replace(/\s+/g, '-');
  }

  // ----------------- Video Selection -----------------
  loadVideoById(videoId: string) {
    const user = JSON.parse(localStorage.getItem('userData') || '{}');
    this.videoService.getVideoDetails(videoId, user.user_id).pipe(takeUntil(this.destroy$)).subscribe({
      next: res => {
        const v = res.video;
        this.selectedVideo = {
          _id: v._id,
          title: v.title,
          description: v.description,
          url: v.url,
          thumbnail: v.thumbnailUrl,
          module: v.project?.name || '',
          date: new Date(v.createdAt).toLocaleDateString(),
          views: res.views ?? 0,
          duration: res.duration ?? 0,
          supportingFiles: v.supportingFiles,
          projectId: v.project?._id || '',
          likesCount: res.likesCount,
          favoritesCount: res.favoritesCount,
          likedByCurrentUser: res.likedByCurrentUser,
          favoritedByCurrentUser: res.favoritedByCurrentUser,
          comments: (res.comments || []).map((c: any) => ({
            _id: c._id,
            text: c.text,
            createdAt: c.createdAt,
            updatedAt: c.updatedAt,
            userName: `${c.user?.first_name || ''} ${c.user?.last_name || ''}`.trim(),
            userEmail: c.user?.primary_email_id || '',
            profile_image: c.user?.profile_image || 'assets/default-avatar.png'
          }))
        };

        if (this.selectedVideo.projectId) {
          this.loadVideosByProject(this.selectedVideo.projectId, this.selectedVideo._id);
        }
      },
      error: err => console.error('Failed to load video details', err)
    });
  }

  loadVideosByProject(projectId: string, excludeVideoId: string) {
    this.videoService.getVideosByProject(projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: res => {
        const filteredVideos = res.videos.filter((v: ApiVideo) => v._id !== excludeVideoId);
        this.videos = this.mapVideos(filteredVideos);
        this.totalCount = res.totalCount;
      },
      error: err => console.error('Failed to load project videos', err)
    });
  }

  selectVideo(video: Video) {
    this.selectedVideo = video;
    this.playerStateChange.emit(true);
    this.router.navigate(['/content', this.currentModule, 'video', video._id]);
  }

  clearSelection() {
    this.selectedVideo = null;
    this.playerStateChange.emit(false);
    this.router.navigate(['/content', this.currentModule, 'videos']);
  }

  getUpNextVideos(): Video[] {
    return this.videos;
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.setupVideoStream(); // reload with new offset
  }

  onLimitChange(newLimit: number) {
    this.limit = newLimit;
    this.currentPage = 1; // reset to first page
    this.setupVideoStream(); // reload with new limit
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
